<template>
  <div class="grid gap-3 rounded-xl border border-dashed bg-muted/30 p-4" data-testid="customer-create-block">
    <div class="flex items-center justify-between gap-2">
      <p class="text-sm font-medium">{{ t('customers.newTitle') }}</p>
      <Button
        type="button" variant="ghost" size="sm"
        class="h-6 px-2 text-xs text-muted-foreground"
        data-testid="cancel-new-customer"
        @click="emit('cancel')"
      >
        {{ t('common.cancel') }}
      </Button>
    </div>

    <!-- WHO the customer IS — an explicit segmented choice, never an
         empty-field-means-default: the dispatcher says whether they are talking to
         a person or a company, and that decision changes which record is born. -->
    <FormField v-slot="{ value, handleChange }" name="newCustomer.kind">
      <FormItem class="grid gap-1.5 space-y-0">
        <FormControl>
          <div class="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" role="group">
            <button
              v-for="option in KINDS" :key="option"
              type="button"
              :class="[
                'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                value === option
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              ]"
              :data-testid="`customer-kind-${option}`"
              @click="handleChange(option)"
            >
              <component :is="option === 'contact' ? User : Building2" class="h-4 w-4 shrink-0" />
              {{ t(`customers.kind.${option}`) }}
            </button>
          </div>
        </FormControl>
      </FormItem>
    </FormField>

    <!-- The COMPANY branch's own fields. The person below is a separate stratum. -->
    <template v-if="kind === 'company'">
      <FormField v-slot="{ componentField }" name="newCustomer.companyName">
        <FormItem class="grid gap-1 space-y-0">
          <FormControl>
            <Input v-bind="componentField" :class="inputClass" :placeholder="t('companies.fields.companyName')" data-testid="new-company-name" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <div class="grid gap-2 sm:grid-cols-2">
        <FormField v-slot="{ componentField }" name="newCustomer.companyPhone">
          <FormItem class="grid gap-1 space-y-0">
            <FormControl>
              <Input v-bind="componentField" :class="inputClass" :placeholder="t('companies.fields.phone')" data-testid="new-company-phone" />
            </FormControl>
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="newCustomer.companyEmail">
          <FormItem class="grid gap-1 space-y-0">
            <FormControl>
              <Input v-bind="componentField" :class="inputClass" type="email" :placeholder="t('companies.fields.email')" data-testid="new-company-email" />
            </FormControl>
          </FormItem>
        </FormField>
      </div>
    </template>

    <!-- THE PERSON. On the contact branch she IS the customer; on the company
         branch she is the personne-ressource — the same fields either way.
         ⭐ OPTIONAL on the company branch, and said so out loud: no person means
         this customer cannot be texted, which is a legitimate outcome. Requiring
         it would stall the call or mint « Gestionnaire / 000-000-0000 », and a
         placeholder number fails SMS silently AND poisons dedup. -->
    <div
      v-show="!personCollapsed"
      v-if="kind === 'company'"
      class="mt-1 flex items-baseline justify-between gap-2 border-t pt-3"
    >
      <p class="text-sm font-medium">{{ t('customers.siteContact') }}</p>
      <span class="text-xs text-muted-foreground">{{ t('customers.optional') }}</span>
    </div>

    <!-- ⭐ COLLAPSED once « C'est Ginette » answered the person question (§55's
         answered-cards-collapse grammar): the identity was PREFILLED into these
         fields from the candidate, so leaving them on screen would invite edits
         the submit chain ignores (the create short-circuits onto the existing
         contact). v-show, not v-if — the values stay live for the schema, the
         title derivation and the company branch's site-contact attach. The
         resolved line below restates the choice; « changer » brings them back. -->
    <div v-show="!personCollapsed" class="grid gap-2 sm:grid-cols-2">
      <FormField v-slot="{ componentField }" name="newCustomer.firstName">
        <FormItem class="grid gap-1 space-y-0">
          <FormControl>
            <Input v-bind="componentField" :class="inputClass" :placeholder="t('contacts.fields.firstName')" data-testid="new-contact-first-name" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="newCustomer.lastName">
        <FormItem class="grid gap-1 space-y-0">
          <FormControl>
            <Input v-bind="componentField" :class="inputClass" :placeholder="t('contacts.fields.lastName')" data-testid="new-contact-last-name" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- TWO numbers, both optional, and the labels carry the difference: a text
         to a landline is delivered nowhere and reports nothing, so the app never
         guesses which kind a number is — the box it was typed into says so. -->
    <div v-show="!personCollapsed" class="grid gap-2 sm:grid-cols-2">
      <FormField v-slot="{ componentField }" name="newCustomer.phone">
        <FormItem class="grid gap-1 space-y-0">
          <FormControl>
            <Input v-bind="componentField" :class="inputClass" :placeholder="t('contacts.fields.phone')" data-testid="new-contact-phone" />
          </FormControl>
        </FormItem>
      </FormField>
      <FormField v-slot="{ componentField }" name="newCustomer.cellPhone">
        <FormItem class="grid gap-1 space-y-0">
          <FormControl>
            <Input
              v-bind="componentField" :class="inputClass"
              :placeholder="t('contacts.fields.cellPhone')" data-testid="new-contact-cell-phone"
              @input="dedup?.onCellInput()"
              @blur="dedup?.checkCell()"
            />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <!-- THE PERSON QUESTION (state 3): this cell already belongs to someone.
         The REASON is on screen (the number is the key), and both verbs are
         NAMED — an ✕ would make the dangerous answer the lazy one. Under the
         claim, « no » necessarily means taking the number from its holder. -->
    <div
      v-if="dedup && dedup.personState.value === 'candidate' && dedup.personCandidate.value"
      class="grid gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950"
      data-testid="person-candidate-prompt"
    >
      <p class="text-sm">
        {{ t('customers.dedup.cellFound', { name: dedup.personCandidate.value.name }) }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          type="button" size="sm" data-testid="use-existing-contact"
          @click="dedup.acceptPerson()"
        >
          {{ t('customers.dedup.itsThem', { name: dedup.personCandidate.value.first_name }) }}
        </Button>
        <Button
          type="button" size="sm" variant="outline" data-testid="release-cell"
          @click="dedup.releasePerson()"
        >
          {{ t('customers.dedup.releaseNumber') }}
        </Button>
      </div>
    </div>
    <!-- Resolved, restatable — the chosen identity stays on screen (state 4). -->
    <div
      v-else-if="dedup && dedup.personAnswer.value && dedup.personAnswer.value.use !== 'new'"
      class="flex items-center justify-between gap-2 text-xs text-muted-foreground"
      data-testid="person-resolved-line"
    >
      <span>
        {{ dedup.personAnswer.value.use === 'existing'
          ? t('customers.dedup.usingContact', { name: dedup.personAnswer.value.candidate.name })
          : t('customers.dedup.numberReleased', { name: dedup.personAnswer.value.candidate.name }) }}
      </span>
      <button type="button" class="underline" data-testid="reopen-person" @click="dedup.reopenPerson()">
        {{ t('customers.dedup.change') }}
      </button>
    </div>

    <!-- WHERE. A new customer arrives with the address that made them one, so the
         two are one block and one « Créer » — there is no second screen. The
         row, its answers and the place question are the shared address fields
         (design 170): number + postal code first, the house restated as the
         registry spells it, and no address saved before it has answered once. -->
    <AddressLookupFields
      v-if="dedup"
      :lookup="dedup.address"
      field-prefix="newAddress."
      :input-class="inputClass"
      save-verb="create"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { User, Building2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form'
import AddressLookupFields from '@/components/shared/AddressLookupFields.vue'
import type { CustomerDedup } from '@/composables/useCustomerDedup'

/**
 * « Créer un client » — the inline block behind « + Nouvelle adresse », shared by
 * the console modal and the handheld drawer (parity is a product law; only the
 * chrome differs).
 *
 * It writes into the PARENT's vee-validate form (`newCustomer.*`,
 * `newAddress.*` — see `useSchemaJob`), so validation, the submit chain and the
 * §55 wipe all stay in one place. The block itself owns no state: `kind` is read
 * back from the form, which is what lets the parent reset it.
 */
const props = defineProps<{
  // The current segment, read from the form so this component holds no copy of it
  // (the template reads it as `kind`).
  kind: 'contact' | 'company'
  // Touch sizing for the handheld drawer (`h-14 rounded-2xl`, the 56px minimum).
  // The same idiom as the comboboxes' `button-class`: one component, two chromes,
  // never an `isMobile` prop.
  inputClass?: string
  // Match-at-entry's state machine, OWNED BY THE PARENT (which owns submit and
  // the create chain) — this block only renders its questions and relays the
  // blur triggers, the same statelessness it keeps toward the form.
  dedup?: CustomerDedup
}>()

const emit = defineEmits<{ cancel: [] }>()

const { t } = useI18n()
const KINDS = ['contact', 'company'] as const

// The §55 collapse grammar: an 'existing' answer folds that question's fields
// to its resolved line — editing them would be theater, the submit chain uses
// the existing record. 'release' / 'new' answers keep the fields: they ARE the
// material of what gets created. (The place question's collapse lives in
// AddressLookupFields.)
const personCollapsed = computed(() => props.dedup?.personAnswer.value?.use === 'existing')
</script>
